import { Inject, Injectable } from '@angular/core';
import { Router, ROUTER_CONFIGURATION } from '@angular/router';
import { AccountingPeriod } from '@assetaccountant/api/models/accounting-period';
import { CalendarV2Api } from '@assetaccountant/api/services/calendar-v2.api';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { RegimeApiModel } from '../models/regime-api-model';
import { Register } from '../models/register';
import { compareProximityToDate, TODAY, toUniversalDateString } from '../utils/date-utils';
import {
  navigateToRouteWithReplacedParameters,
  ROUTE_PERIOD_SEPARATOR,
  TypedActivatedRouteSnapshot,
} from '../utils/route-utils';
import { RegimeData, RegimeParams } from './regime.resolver';

export type PeriodParams = RegimeParams & {
  periodRange: string;
};

export type PeriodData = RegimeData & {
  period: { startDate: string; endDate: string };
};

interface ValidDateRange {
  startDate: string;
  endDate: string;
  changed: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PeriodResolver {
  private static _periods$:
    | (Observable<AccountingPeriod[]> & {
        registerId?: number;
        regimeId?: number;
        timestamp?: number;
        startDate?: string;
        endDate?: string;
      })
    | undefined;

  public static get periods$() {
    return PeriodResolver._periods$;
  }

  public static clearCache() {
    PeriodResolver._periods$ = undefined;
  }

  private listPeriods(registerId: number, regimeId: number, dateRange: ValidDateRange | null) {
    if (
      !PeriodResolver._periods$ ||
      PeriodResolver._periods$.registerId !== registerId ||
      PeriodResolver._periods$.regimeId !== regimeId ||
      // Older than an hour
      (PeriodResolver._periods$.timestamp ?? 0) < new Date().valueOf() - 60 * 60 * 1000 ||
      // Outside date range
      !this.datesInRange(dateRange)
    ) {
      // Extend the date range 20 years past the greater of today or requested date range
      const startDate = toUniversalDateString(
        moment.min(moment.utc(dateRange?.startDate), moment.utc()).add(-20, 'years'),
        {
          throwOnFailure: true,
        },
      );
      const endDate = toUniversalDateString(
        moment.max(moment.utc(dateRange?.endDate), moment.utc()).add(20, 'years'),
        {
          throwOnFailure: true,
        },
      );

      PeriodResolver._periods$ = this.calendarApi
        .getPeriods({
          registerId,
          regimeId,
          startDate,
          endDate,
        })
        .pipe(
          tap(({ periods }) => {
            (PeriodResolver._periods$ as any).startDate =
              periods.length > 0 ? periods[0].startDate : null;
            (PeriodResolver._periods$ as any).endDate =
              periods.length > 0 ? periods[periods.length - 1].endDate : null;
          }),
          map(({ periods }) => periods),
          shareReplay(),
        );

      PeriodResolver._periods$.registerId = registerId;
      PeriodResolver._periods$.regimeId = regimeId;
      PeriodResolver._periods$.timestamp = new Date().valueOf();
    }

    return PeriodResolver._periods$;
  }

  get lastYear() {
    return this.today.replace(
      /^(\d+)(-\d+-)\d+$/,
      (_, year, month) => (+year - 1).toString() + month + '01',
    );
  }
  get nextYear() {
    return this.today.replace(
      /^(\d+)(-\d+-)\d+$/,
      (_, year, month) => (+year + 1).toString() + month + '01',
    );
  }

  constructor(
    private router: Router,
    private calendarApi: CalendarV2Api,
    @Inject(ROUTER_CONFIGURATION) private routerConfig: { enableTracing: boolean },
    @Inject(TODAY) private today: string,
  ) {}

  resolve(route: TypedActivatedRouteSnapshot<PeriodParams>) {
    if (!route.parent) {
      throw new Error('No parent route');
    }

    const register: Register = route.parent.data.register;
    const regime: RegimeApiModel = route.parent.data.regime;

    if (!register) {
      throw new Error('No register data');
    }
    if (!regime) {
      throw new Error('No regime data');
    }

    const periodRange = parsePeriodRange(route.params.periodRange || '');

    let result = this.getValidResult({
      startDate: toUniversalDateString(periodRange[0]),
      endDate: toUniversalDateString(periodRange[1]),
    });

    return this.listPeriods(register.id, regime.id, result).pipe(
      map(periods => {
        if (result == null) {
          result = this.getDefaultResult(periods);
        }

        result = this.adjustResult(result, periods);

        if (result.changed) {
          if (this.routerConfig.enableTracing) {
            console.log('Period Resolver: redirecting', result);
          }

          navigateToRouteWithReplacedParameters(
            this.router,
            {
              periodRange: createPeriodRange(result.startDate, result.endDate),
            },
            route,
          );

          return null as never;
        }

        if (this.routerConfig.enableTracing) {
          console.log('Period Resolver: resolved', result);
        }
        sessionStorage.setItem('periodStart', result.startDate);
        sessionStorage.setItem('periodEnd', result.endDate);
        localStorage.setItem('periodStart', result.startDate);
        localStorage.setItem('periodEnd', result.endDate);

        return {
          startDate: result.startDate,
          endDate: result.endDate,
        };
      }),
    );
  }

  private isResultValid(result: {
    startDate: string | null;
    endDate: string | null;
  }): result is { startDate: string; endDate: string } {
    if (!result.startDate || !result.endDate || result.startDate >= result.endDate) {
      return false;
    }

    return true;
  }

  private getValidResult(initialResult: {
    startDate: string | null;
    endDate: string | null;
  }): ValidDateRange | null {
    let result = {
      ...initialResult,
      changed: false,
    };
    if (this.isResultValid(result)) {
      return result;
    }

    // Try to get dates from session
    result = {
      startDate: toUniversalDateString(sessionStorage.getItem('periodStart')),
      endDate: toUniversalDateString(sessionStorage.getItem('periodEnd')),
      changed: true,
    };
    if (this.isResultValid(result)) {
      return result;
    }

    // Try to get dates from local storage
    result = {
      startDate: toUniversalDateString(localStorage.getItem('periodStart')),
      endDate: toUniversalDateString(localStorage.getItem('periodEnd')),
      changed: true,
    };
    if (this.isResultValid(result)) {
      return result;
    }

    return null;
  }

  private getDefaultResult(periods: AccountingPeriod[]): ValidDateRange {
    const period =
      periods.find(
        p =>
          toUniversalDateString(p.startDate, { throwOnFailure: true }) <= this.today &&
          this.today <= toUniversalDateString(p.endDate, { throwOnFailure: true }),
      ) ?? periods[Math.floor(periods.length / 2)];
    const result = {
      startDate: period && toUniversalDateString(period.startDate),
      endDate: period && toUniversalDateString(period.endDate),
      changed: true,
    };

    if (this.isResultValid(result)) {
      return result;
    }

    throw new Error('Invalid period');
  }

  private adjustResult(
    result: { startDate: string; endDate: string; changed: boolean },
    periods: AccountingPeriod[],
  ): ValidDateRange {
    if (periods.length === 0) {
      return result;
    }

    let startPeriod = periods.find(cp => cp.startDate === result.startDate);
    let endPeriod = periods.find(cp => cp.endDate === result.endDate);

    if (!startPeriod || !endPeriod) {
      const startDate = result.startDate;
      const endDate = result.endDate;
      if (!startPeriod) {
        startPeriod = periods.slice().sort(compareProximityToDate(startDate, 'startDate'))[0];

        if (!startPeriod) {
          throw new Error('Could not get a start date');
        }
      }
      if (!endPeriod) {
        endPeriod = periods
          .filter(cp => startPeriod && cp.endDate > startPeriod.startDate)
          .sort(compareProximityToDate(endDate, 'endDate'))[0];

        if (!endPeriod) {
          throw new Error('Could not get an end date');
        }
      }

      result = {
        startDate: startPeriod.startDate,
        endDate: endPeriod.endDate,
        changed: true,
      };
    }

    return result;
  }

  private datesInRange(dateRange: ValidDateRange | null): boolean {
    if (PeriodResolver._periods$?.startDate && PeriodResolver._periods$?.endDate && dateRange) {
      return (
        PeriodResolver._periods$.startDate <= dateRange.startDate &&
        PeriodResolver._periods$.endDate >= dateRange.endDate
      );
    }
    return false;
  }
}

export function parsePeriodRange(periodRange: string): [string, string] | [null, null] {
  const regularExpression = new RegExp(
    '^(\\d{4})-(\\d{2})-(\\d{2})' +
      ROUTE_PERIOD_SEPARATOR +
      '(?:(?:(\\d{4})-)?(\\d{2})-)?(\\d{2})$',
  );
  const match = regularExpression.exec(periodRange);

  if (!match) {
    return [null, null];
  }
  const [, fromYear, fromMonth, fromDay, toYear, toMonth, toDay] = match;
  return [
    `${fromYear}-${fromMonth}-${fromDay}`,
    `${toYear || fromYear}-${toMonth || fromMonth}-${toDay}`,
  ];
}

export function createPeriodRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start || !end) {
    return '-';
  }
  const match = /^(\d{4})-(\d{2})-/.exec(start);
  if (!match) {
    return '-';
  }
  const [, fromYear, fromMonth] = match;

  return `${start}${ROUTE_PERIOD_SEPARATOR}${end}`
    .replace(`${ROUTE_PERIOD_SEPARATOR}${fromYear}-`, ROUTE_PERIOD_SEPARATOR)
    .replace(`${ROUTE_PERIOD_SEPARATOR}${fromMonth}-`, ROUTE_PERIOD_SEPARATOR);
}
